import { useState } from 'react';
import { Link } from 'react-router-dom';
import { GlassCard } from '../components/Layout';
import {
  DollarSign, TrendingUp, Target, PiggyBank, BarChart3,
  GraduationCap, Shield, ArrowRight, Check, Lock, Sparkles,
  Building, Calendar, ChevronRight, AlertTriangle, CreditCard
} from 'lucide-react';
import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const DEMO_FAMILY = {
  parent: 'Sarah M.',
  children: [
    { name: 'Riley', grade: '11th', school: 'Duke University', path: 'College Bound', daysToGrad: 440, color: '#3b82f6' },
    { name: 'Jordan', grade: '9th', school: 'Georgia Tech', path: 'Engineering', daysToGrad: 1170, color: '#8b5cf6' },
  ],
  accounts: {
    total_saved: 89371,
    checking: 12400,
    savings_529_riley: 45200,
    savings_529_jordan: 18500,
    high_yield: 13271,
  },
  costs: {
    riley: { total_4yr: 287000, saved: 45200, gap: 241800, scholarships: 32000 },
    jordan: { total_4yr: 196000, saved: 18500, gap: 177500, scholarships: 0 },
  },
};

const SAVINGS_CHART = [
  { month: 'Jul', amount: 72000 },
  { month: 'Aug', amount: 74500 },
  { month: 'Sep', amount: 77200 },
  { month: 'Oct', amount: 80100 },
  { month: 'Nov', amount: 83800 },
  { month: 'Dec', amount: 86500 },
  { month: 'Jan', amount: 89371 },
];

const FUNDING_PIE = [
  { name: '529 Plans', value: 63700, color: '#3b82f6' },
  { name: 'Scholarships', value: 32000, color: '#10b981' },
  { name: 'Savings', value: 13271, color: '#8b5cf6' },
  { name: 'Gap Remaining', value: 178029, color: '#C6D4E8' },
];

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

export default function FinancialPreviewPage() {
  const [locked] = useState(true);

  return (
    <div className="min-h-screen bg-navy-gradient">
      {/* Hero */}
      <section className="pt-28 pb-16 px-6 md:px-12 text-center relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-emerald-600/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-blue-600/20 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm mb-6">
              <DollarSign className="w-4 h-4" /> Financial Intelligence Dashboard
            </div>
            <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-white mb-4">
              See Your Family's Complete Financial Picture
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Connect your accounts to track savings, identify funding gaps, and get AI-powered recommendations, all in one place.
            </p>
            <p className="text-sm text-slate-300 mb-10">
              Below is a <strong className="text-slate-300">sample dashboard</strong> showing what your experience will look like.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Demo Dashboard */}
      <section className="px-4 md:px-12 pb-16 max-w-7xl mx-auto space-y-6">
        {/* Sample badge */}
        <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm" data-testid="demo-badge">
          <AlertTriangle className="w-4 h-4" />
          <span>This is a preview with sample data. <Link to="/pricing" className="underline font-medium">Sign up</Link> and connect your accounts to see your real numbers.</span>
        </div>

        {/* Family Overview Cards */}
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { label: 'Total Saved', value: fmt(DEMO_FAMILY.accounts.total_saved), icon: PiggyBank, color: 'emerald', change: '+$2,871 this month' },
            { label: 'Total Gap', value: fmt(DEMO_FAMILY.costs.riley.gap + DEMO_FAMILY.costs.jordan.gap), icon: Target, color: 'red', change: 'Across 2 children' },
            { label: 'Scholarships Found', value: fmt(32000), icon: GraduationCap, color: 'blue', change: '12 matches' },
            { label: 'Monthly Contribution', value: '$1,850', icon: TrendingUp, color: 'purple', change: 'On track' },
          ].map((card, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
              <GlassCard className="relative overflow-hidden" data-testid={`demo-stat-${i}`}>
                {locked && (
                  <div className="absolute top-2 right-2"><Lock className="w-3.5 h-3.5 text-slate-300" /></div>
                )}
                <card.icon className={`w-8 h-8 text-${card.color}-400 mb-3`} />
                <p className="text-2xl font-bold text-white">{card.value}</p>
                <p className="text-slate-300 text-sm">{card.label}</p>
                <p className={`text-xs mt-1 text-${card.color}-400`}>{card.change}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left - Children Progress */}
          <div className="lg:col-span-2 space-y-4">
            {DEMO_FAMILY.children.map((child, i) => {
              const costs = i === 0 ? DEMO_FAMILY.costs.riley : DEMO_FAMILY.costs.jordan;
              const pct = Math.round((costs.saved / costs.total_4yr) * 100);
              return (
                <motion.div key={child.name} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 + i * 0.15 }}>
                  <GlassCard data-testid={`demo-child-${child.name.toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: child.color }}>
                          {child.name[0]}
                        </div>
                        <div>
                          <p className="text-white font-medium">{child.name}, {child.grade} Grade</p>
                          <p className="text-slate-300 text-sm">{child.path} &bull; {child.school}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">{child.daysToGrad} days</p>
                        <p className="text-slate-300 text-xs">to graduation</p>
                      </div>
                    </div>

                    {/* Funding bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-emerald-400">{fmt(costs.saved)} saved</span>
                        <span className="text-slate-300">{fmt(costs.total_4yr)} total</span>
                      </div>
                      <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: child.color }} />
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-300">{pct}% funded</span>
                        <span className="text-red-400">Gap: {fmt(costs.gap)}</span>
                      </div>
                    </div>

                    {costs.scholarships > 0 && (
                      <div className="mt-3 flex items-center gap-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <GraduationCap className="w-4 h-4 text-emerald-400" />
                        <span className="text-emerald-400 text-sm">{fmt(costs.scholarships)} in scholarship matches found</span>
                      </div>
                    )}
                  </GlassCard>
                </motion.div>
              );
            })}

            {/* Savings Trend Chart */}
            <GlassCard>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold">Savings Growth</h3>
                <span className="text-emerald-400 text-sm">+{fmt(17371)} (6 mo)</span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={SAVINGS_CHART}>
                  <XAxis dataKey="month" stroke="#C6D4E8" fontSize={12} />
                  <YAxis stroke="#C6D4E8" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                  <Bar dataKey="amount" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </GlassCard>
          </div>

          {/* Right - Funding Breakdown */}
          <div className="space-y-4">
            <GlassCard>
              <h3 className="text-white font-semibold mb-4">Funding Breakdown</h3>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={FUNDING_PIE} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" stroke="none">
                    {FUNDING_PIE.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={v => fmt(v)} contentStyle={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {FUNDING_PIE.map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-slate-300">{item.name}</span>
                    </div>
                    <span className="text-white font-medium">{fmt(item.value)}</span>
                  </div>
                ))}
              </div>
            </GlassCard>

            {/* Connected Accounts Preview */}
            <GlassCard>
              <h3 className="text-white font-semibold mb-3">Connected Accounts</h3>
              <div className="space-y-2">
                {[
                  { name: 'Chase Checking', bal: '$12,400', icon: Building, color: 'blue' },
                  { name: 'Riley 529 Plan', bal: '$45,200', icon: GraduationCap, color: 'emerald' },
                  { name: 'Jordan 529 Plan', bal: '$18,500', icon: GraduationCap, color: 'purple' },
                  { name: 'Marcus HYSA', bal: '$13,271', icon: TrendingUp, color: 'cyan' },
                ].map((acct, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-800/50 border border-slate-700/50">
                    <div className="flex items-center gap-2.5">
                      <acct.icon className={`w-4 h-4 text-${acct.color}-400`} />
                      <span className="text-slate-300 text-sm">{acct.name}</span>
                    </div>
                    <span className="text-white text-sm font-medium">{acct.bal}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 p-2.5 rounded-lg border-2 border-dashed border-slate-600 text-center">
                <Lock className="w-4 h-4 text-slate-300 mx-auto mb-1" />
                <p className="text-slate-300 text-xs">Connect via Plaid to see your real accounts</p>
              </div>
            </GlassCard>

            {/* Credit Score Preview */}
            <GlassCard>
              <h3 className="text-white font-semibold mb-3">Credit & Loans</h3>
              <div className="text-center p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 mb-3">
                <p className="text-4xl font-bold text-emerald-400">742</p>
                <p className="text-slate-300 text-sm">Sample Credit Score</p>
                <div className="h-2 bg-slate-700 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-red-500 via-amber-500 to-emerald-500 rounded-full" style={{ width: '82%' }} />
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-300">Student Loans</span><span className="text-white">$0</span></div>
                <div className="flex justify-between"><span className="text-slate-300">Parent PLUS Eligible</span><span className="text-emerald-400">Yes</span></div>
                <div className="flex justify-between"><span className="text-slate-300">Est. Rate</span><span className="text-white">6.28%</span></div>
              </div>
              <p className="text-xs text-slate-300 mt-2">*Requires Experian Connect</p>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 md:px-12 pb-24">
        <div className="max-w-4xl mx-auto">
          <GlassCard className="text-center p-10">
            <Sparkles className="w-10 h-10 text-blue-400 mx-auto mb-4" />
            <h2 className="font-outfit text-2xl sm:text-3xl font-semibold text-white mb-3">
              Ready to See Your Real Numbers?
            </h2>
            <p className="text-slate-300 mb-6 max-w-xl mx-auto">
              Create an account, connect your financial accounts securely via Plaid, and get personalized AI recommendations to close your funding gap.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/pricing" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-emerald-600 to-blue-600 text-white font-semibold hover:from-emerald-700 hover:to-blue-700 transition-all text-lg" data-testid="preview-cta-signup">
                Start Free <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/pricing" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl border-2 border-slate-600 text-white font-semibold hover:border-blue-500/50 hover:bg-blue-500/10 transition-all text-lg">
                View Plans
              </Link>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 mt-6 text-sm text-slate-300">
              <span className="flex items-center gap-1"><Shield className="w-4 h-4 text-emerald-400" /> Bank-level encryption</span>
              <span className="flex items-center gap-1"><Check className="w-4 h-4 text-emerald-400" /> Read-only access</span>
              <span className="flex items-center gap-1"><Lock className="w-4 h-4 text-emerald-400" /> Never stores credentials</span>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  );
}
